/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Select } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState, useRef } from 'react'
import { ErrorLabel } from '../ErrorLabel'
import Text from '../Text'
import { weightRegular } from 'src/components/css'
import { paragraphMedium } from 'src/components/css'

type DateOfBirthInputProps = {
  name: string
  value: string // YYYY-MM-DD format
  setValue: (value: string) => void
  fieldErrors: any
  customError: any
  label: string
  disabled?: boolean
  tabIndex?: number
  /** Tab index for month dropdown */
  monthTabIndex?: number
  /** Tab index for day dropdown */
  dayTabIndex?: number
  /** Tab index for year dropdown */
  yearTabIndex?: number
}

/**
 * DateOfBirthInput component that allows users to select their date of birth
 * using month, day, and year dropdowns. All dropdowns support search functionality
 * for improved user experience and accessibility.
 */
export const DateOfBirthInput: React.FC<DateOfBirthInputProps> = ({
  name,
  value,
  setValue,
  fieldErrors,
  customError,
  label,
  disabled = false,
  // Default to 0 so the three selects stay in natural document tab order. A
  // positive default (e.g. 1) yanks them to the front of the page's tab
  // sequence, which makes Tab "jump" out of the form when leaving DOB. Callers
  // that orchestrate explicit ordering (DynamicForm) pass the *TabIndex props.
  tabIndex = 0,
  monthTabIndex,
  dayTabIndex,
  yearTabIndex,
}) => {
  const [month, setMonth] = useState('')
  const [day, setDay] = useState('')
  const [year, setYear] = useState('')

  // Hidden input ref for browser autofill detection
  const hiddenDateRef = useRef<HTMLInputElement>(null)

  // Track dropdown open state and search values for Tab-to-select
  const [monthOpen, setMonthOpen] = useState(false)
  const [dayOpen, setDayOpen] = useState(false)
  const [yearOpen, setYearOpen] = useState(false)
  const monthSearchRef = useRef('')
  const daySearchRef = useRef('')
  const yearSearchRef = useRef('')

  // Initialize component state from value prop
  useEffect(() => {
    if (value) {
      const [yearVal, monthVal, dayVal] = value.split('-')
      setYear(yearVal || '')
      setMonth(monthVal || '')
      setDay(dayVal || '')
    } else {
      setYear('')
      setMonth('')
      setDay('')
    }
  }, [value])

  // Detect browser autofill on the hidden native input
  useEffect(() => {
    const el = hiddenDateRef.current
    if (!el) {
      return
    }

    const handleAutofill = () => {
      const val = el.value
      if (!val) {
        return
      }
      const parsed = dayjs(val)
      if (!parsed.isValid()) {
        return
      }
      const y = parsed.format('YYYY')
      const m = parsed.format('MM')
      const d = parsed.format('DD')
      setYear(y)
      setMonth(m)
      setDay(d)
      setValue(`${y}-${m}-${d}`)
    }

    el.addEventListener('change', handleAutofill)
    el.addEventListener('input', handleAutofill)

    // Poll briefly to catch autofill that doesn't fire events
    const interval = setInterval(() => {
      if (el.value) {
        handleAutofill()
        clearInterval(interval)
      }
    }, 500)
    const timeout = setTimeout(() => clearInterval(interval), 5000)

    return () => {
      el.removeEventListener('change', handleAutofill)
      el.removeEventListener('input', handleAutofill)
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [])

  /**
   * Handles month selection change and updates the value
   * @param newMonth - The selected month value
   */
  const handleMonthChange = (newMonth: string) => {
    setMonth(newMonth)
    // Reset day if current selection is invalid for new month
    if (day) {
      const daysInMonth = dayjs(`${year}-${newMonth}-01`).daysInMonth()
      if (parseInt(day, 10) > daysInMonth) {
        setDay('')
      }
    }
    updateValue(year, newMonth, day)
  }

  /**
   * Handles day selection change and updates the value
   * @param newDay - The selected day value
   */
  const handleDayChange = (newDay: string) => {
    setDay(newDay)
    updateValue(year, month, newDay)
  }

  /**
   * Handles year selection change and updates the value
   * @param newYear - The selected year value
   */
  const handleYearChange = (newYear: string) => {
    setYear(newYear)
    updateValue(newYear, month, day)
  }

  /**
   * Updates the complete date value when any component changes
   * @param yearVal - The year value
   * @param monthVal - The month value
   * @param dayVal - The day value
   */
  const updateValue = (yearVal: string, monthVal: string, dayVal: string) => {
    if (yearVal && monthVal && dayVal) {
      setValue(`${yearVal}-${monthVal}-${dayVal}`)
    } else {
      setValue('')
    }
  }

  // Generate year options from 1900 to current year
  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: currentYear - 1899 }, (_, i) => ({
    label: (currentYear - i).toString(),
    value: (currentYear - i).toString(),
  }))

  // Month options for Tab-to-select lookup
  const monthOptions = [
    { label: 'January', value: '01' },
    { label: 'February', value: '02' },
    { label: 'March', value: '03' },
    { label: 'April', value: '04' },
    { label: 'May', value: '05' },
    { label: 'June', value: '06' },
    { label: 'July', value: '07' },
    { label: 'August', value: '08' },
    { label: 'September', value: '09' },
    { label: 'October', value: '10' },
    { label: 'November', value: '11' },
    { label: 'December', value: '12' },
  ]

  // Filter function for months
  const filterMonth = (
    input: string,
    option: { label: string; value: string }
  ) => {
    if (!input || !option) {
      return false
    }
    if (option.label.toLowerCase().includes(input.toLowerCase())) {
      return true
    }
    const monthNumber = parseInt(option.value, 10)
    const inputNumber = parseInt(input, 10)
    return (
      !isNaN(inputNumber) &&
      (monthNumber === inputNumber || monthNumber.toString().startsWith(input))
    )
  }

  // Day options
  const dayOptions = Array.from(
    { length: dayjs(`${year}-${month}-01`).daysInMonth() || 31 },
    (_, i) => ({
      label: (i + 1).toString(),
      value: (i + 1).toString().padStart(2, '0'),
    })
  )

  // Filter function for days
  const filterDay = (input: string, option: { label: string; value: string }) =>
    !!input && !!option && option.label.includes(input)

  // Filter function for years
  const filterYear = (
    input: string,
    option: { label: string; value: string }
  ) => !!input && !!option && option.label.includes(input)

  // Tab-to-select handlers
  const handleMonthKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && monthOpen && monthSearchRef.current) {
      const matching = monthOptions.find((opt) =>
        filterMonth(monthSearchRef.current, opt)
      )
      if (matching) {
        e.preventDefault()
        handleMonthChange(matching.value)
        setMonthOpen(false)
      }
    }
  }

  const handleDayKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && dayOpen && daySearchRef.current) {
      const matching = dayOptions.find((opt) =>
        filterDay(daySearchRef.current, opt)
      )
      if (matching) {
        e.preventDefault()
        handleDayChange(matching.value)
        setDayOpen(false)
      }
    }
  }

  const handleYearKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && yearOpen && yearSearchRef.current) {
      const matching = yearOptions.find((opt) =>
        filterYear(yearSearchRef.current, opt)
      )
      if (matching) {
        e.preventDefault()
        handleYearChange(matching.value)
        setYearOpen(false)
      }
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
        width: 100%;
      `}
    >
      <style>
        {`
          .ant-select-dropdown {
            z-index: 999999 !important;
          }
          .ant-select-selector {
            background: ${colors.SECONDARY_200} !important;
            border-radius: 8px !important;
          }
          .ant-select-selection-item {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
          .ant-select-dropdown .ant-select-item-option-content {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
        `}
      </style>
      {/* Hidden native input for browser/password-manager autofill detection */}
      <input
        ref={hiddenDateRef}
        type='date'
        name={name}
        autoComplete='bday'
        tabIndex={-1}
        aria-hidden='true'
        css={css`
          position: absolute;
          width: 0;
          height: 0;
          opacity: 0;
          pointer-events: none;
          overflow: hidden;
        `}
      />

      <Text
        variant='paragraphSmall'
        weight='regular'
        color={colors.WHITE}
        style={`
          align-self: stretch;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `}
      >
        {label}
      </Text>

      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          height: 40px;
          width: 100%;
          flex-direction: row;
          @media (max-width: 600px) {
            display: flex;
            flex-direction: column;
            width: 100%;
            align-self: stretch;
            height: auto;
          }
        `}
      >
        {/* Day → Month → Year order (DD/MM/YYYY) to match the mobile app. The
            stored value stays YYYY-MM-DD; only the visual field order changes. */}
        <Select
          placeholder='Day'
          options={dayOptions}
          defaultValue={day || undefined}
          value={day || undefined}
          onChange={handleDayChange}
          disabled={disabled}
          tabIndex={dayTabIndex || tabIndex}
          showSearch
          open={dayOpen}
          onDropdownVisibleChange={setDayOpen}
          onSearch={(val) => {
            daySearchRef.current = val
          }}
          onKeyDown={handleDayKeyDown}
          filterOption={(input, option) =>
            filterDay(input, option as { label: string; value: string })
          }
          status={
            fieldErrors[name] || fieldErrors?.includes(name) ? 'error' : ''
          }
          css={css`
            flex: 1;
            width: 100%;
            @media (max-width: 600px) {
              width: 100%;
              margin-bottom: 8px;
            }
            ${paragraphMedium}
            ${weightRegular}
            font-size: 16px !important;
            color: ${colors.WHITE};
            width: 100%;
            background: ${colors.SECONDARY_200} !important;
            border-radius: 8px !important;
            height: 40px !important;
            ${disabled && 'opacity: 0.5 !important'}
          `}
        />

        <Select
          options={monthOptions}
          defaultValue={month || undefined}
          value={month || undefined}
          onChange={handleMonthChange}
          disabled={disabled}
          tabIndex={monthTabIndex || tabIndex}
          showSearch
          open={monthOpen}
          onDropdownVisibleChange={setMonthOpen}
          onSearch={(val) => {
            monthSearchRef.current = val
          }}
          onKeyDown={handleMonthKeyDown}
          filterOption={(input, option) =>
            filterMonth(input, option as { label: string; value: string })
          }
          status={
            fieldErrors[name] || fieldErrors?.includes(name) ? 'error' : ''
          }
          css={css`
            flex: 1;
            min-width: 120px;
            @media (max-width: 600px) {
              width: 100%;
              margin-bottom: 8px;
            }
            ${paragraphMedium}
            ${weightRegular}
            font-size: 16px !important;
            color: ${colors.WHITE};
            width: 100%;
            background: ${colors.SECONDARY_200} !important;
            border-radius: 8px !important;
            height: 40px !important;
            ${disabled && 'opacity: 0.5 !important'}
          `}
          placeholder='Month'
        />

        <Select
          options={yearOptions}
          defaultValue={year || undefined}
          value={year || undefined}
          onChange={handleYearChange}
          disabled={disabled}
          showSearch
          tabIndex={yearTabIndex || tabIndex}
          open={yearOpen}
          onDropdownVisibleChange={setYearOpen}
          onSearch={(val) => {
            yearSearchRef.current = val
          }}
          onKeyDown={handleYearKeyDown}
          filterOption={(input, option) =>
            filterYear(input, option as { label: string; value: string })
          }
          status={
            fieldErrors[name] || fieldErrors?.includes(name) ? 'error' : ''
          }
          css={css`
            min-width: 120px;
            flex: 1;
            font-size: 14px !important;
            height: 40px !important;
            border-radius: 8px;
            border: 1px solid ${colors.SECONDARY_100};
            background: ${colors.SECONDARY_200};
            width: 100%;
            @media (max-width: 600px) {
              width: 100%;
            }
            ${disabled &&
            css`
              color: ${colors.NEUTRAL_100};
              opacity: 0.5;
            `}
          `}
          placeholder='Year'
        />
      </div>

      <ErrorLabel
        fieldErrors={fieldErrors}
        customError={customError}
        name={name}
      />
    </div>
  )
}
